import React from 'react';
import { NumericTextBoxComponent } from '@syncfusion/ej2-react-inputs';
import { DropDownListComponent } from '@syncfusion/ej2-react-dropdowns';
import { DateTimePickerComponent } from '@syncfusion/ej2-react-calendars';
import { type CrudFormProps } from '../../components/CrudPage';
import { type StockLevelDto, type MaterialDto } from '../../api';
import { FormDialog } from '../../components/FormDialog';
import { type UseFormRegister, type FormState, type UseFormSetValue, type UseFormWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { resolveValidationMessage } from '../../utils/validationMessages';

// Build a local ISO datetime string (no timezone offset) for the backend LocalDateTime field.
// Date.toISOString() would emit UTC with a trailing "Z" and milliseconds, which either fails the
// LocalDateTime parse or shifts the recorded wall-clock time by the UTC offset.
const toLocalIsoDateTime = (d: Date): string => {
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

type StockLevelWithId = StockLevelDto & { id: string };

interface FormProps {
  register: UseFormRegister<StockLevelWithId>;
  formState: FormState<StockLevelWithId>;
  setValue: UseFormSetValue<StockLevelWithId>;
  watch: UseFormWatch<StockLevelWithId>;
}

interface StockLevelFormDialogProps extends CrudFormProps<StockLevelWithId> {
  materials: MaterialDto[];
}

const StockLevelFormDialog: React.FC<StockLevelFormDialogProps> = ({
  value,
  onClose,
  onSave,
  materials
}) => {
  const { t, i18n } = useTranslation(['common']);
  const isEditMode = !!value?.id;

  // Format number according to current locale
  const getNumberFormat = () => {
    return i18n.language === 'de' ? 'N2' : 'N2'; // German uses comma as decimal separator
  };

  return (
    <FormDialog
      value={value}
      onClose={onClose}
      onSave={onSave}
      titleKey="dialogs.stockLevel"
    >
      {({ register, formState: { errors }, setValue, watch }: FormProps) => {
        const currentValue = watch();
        
        return (
          <>
            <input type="hidden" {...register('materialId', { required: t('validation.required') })} />
            <input type="hidden" {...register('recordedAt', { required: t('validation.required') })} />
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('stockLevel.form.material')} *
              </label>
              <DropDownListComponent
                dataSource={materials}
                fields={{ text: 'name', value: 'uuid' }}
                floatLabelType="Never"
                cssClass={errors.materialId ? 'e-error' : ''}
                value={currentValue.materialId}
                onChange={(e: any) => setValue('materialId', e.value, { shouldValidate: true, shouldDirty: true })}
                enabled={!isEditMode}
              />
              {errors.materialId && (
                <div className="text-red-500 text-sm mt-1">
                  {resolveValidationMessage(t, errors.materialId)}
                </div>
              )}
              {isEditMode && (
                <div className="text-gray-500 text-sm mt-1">{t('stockLevel.form.materialNotEditable')}</div>
              )}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('stockLevel.form.recordedAt')} *
              </label>
              <DateTimePickerComponent
                floatLabelType="Never"
                cssClass={errors.recordedAt ? 'e-error' : ''}
                format={i18n.language === 'de' ? 'dd.MM.yyyy HH:mm' : 'MM/dd/yyyy hh:mm a'}
                value={currentValue.recordedAt ? new Date(currentValue.recordedAt) : undefined}
                onChange={(e: any) => setValue('recordedAt', e.value ? toLocalIsoDateTime(e.value) : undefined, { shouldValidate: true, shouldDirty: true })}
                enabled={!isEditMode}
              />
              {errors.recordedAt && (
                <div className="text-red-500 text-sm mt-1">
                  {resolveValidationMessage(t, errors.recordedAt)}
                </div>
              )}
              {isEditMode && (
                <div className="text-gray-500 text-sm mt-1">{t('stockLevel.form.recordedAtNotEditable')}</div>
              )}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('stockLevel.form.currentStock')} *
              </label>
              <NumericTextBoxComponent
                {...register('currentStock', { required: t('validation.required'), valueAsNumber: true })}
                format={getNumberFormat()}
                min={0}
                step={0.01}
                floatLabelType="Never"
                cssClass={errors.currentStock ? 'e-error' : ''}
              />
              {errors.currentStock && (
                <div className="text-red-500 text-sm mt-1">
                  {resolveValidationMessage(t, errors.currentStock)}
                </div>
              )}
            </div>
          </>
        );
      }}
    </FormDialog>
  );
};

export default StockLevelFormDialog; 